import {
  BackArrowIcon,
  Button,
  CircleCheckIcon,
  CircleIcon,
  EyeIcon,
  KeyRoundIcon,
  TextInput,
  Typography,
} from "@/components/atoms";
import { OkDialog } from "@/components/molecules/dialogs/OkDialog";
import { BaseHeader } from "@/components/molecules/header/BaseHeader";
import { LogoHeader } from "@/components/molecules/header/LogoHeader";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useTranslation, useUIColors } from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { router, useNavigation, usePathname } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export interface PasswordSetScreenProps {
  mode?: "set" | "change" | "forgot";
  currentPassword?: string; // For change password flow - current password already verified
  onPasswordSet?: (password: string) => void | Promise<void>;
  onBackPress?: () => void;
  loading?: boolean; // Loading state for API calls
}

interface PasswordValidation {
  hasCapital: boolean;
  hasSmall: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
}

const validatePassword = (password: string): PasswordValidation => {
  return {
    hasCapital: /[A-Z]/.test(password),
    hasSmall: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
};

const isPasswordValid = (validation: PasswordValidation): boolean => {
  return (
    validation.hasCapital &&
    validation.hasSmall &&
    validation.hasNumber &&
    validation.hasSymbol
  );
};

export const PasswordSetScreen = React.memo<PasswordSetScreenProps>(
  function PasswordSetScreen({
    mode = "set",
    currentPassword,
    onPasswordSet,
    onBackPress,
    loading = false,
  }) {
    const { t } = useTranslation();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const pathname = usePathname();

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const passwordValidation = useMemo(
      () => validatePassword(password),
      [password],
    );
    const passwordsMatch = useMemo(
      () => password === confirmPassword && confirmPassword.length > 0,
      [password, confirmPassword],
    );
    const isFormValid = useMemo(
      () => isPasswordValid(passwordValidation) && passwordsMatch,
      [passwordValidation, passwordsMatch],
    );

    const handleConfirm = useCallback(async () => {
      if (!isFormValid) return;

      // For change password mode, validate that new password is different from current
      if (
        mode === "change" &&
        currentPassword &&
        password === currentPassword
      ) {
        setErrorMessage(
          t("auth.passwordSameAsCurrent") ||
            "New password and current password cannot be the same",
        );
        setErrorDialogVisible(true);
        return;
      }

      trackCatalogEvent({
        screen: DT_SCREEN.REGISTRATION,
        actionName: 'password_set_screen_submit',
        eventType: 'action',
      });
      if (onPasswordSet) {
        await onPasswordSet(password);
      }
    }, [isFormValid, password, currentPassword, mode, onPasswordSet, t]);

    const handleBackPress = useCallback(() => {
      if (onBackPress) {
        onBackPress();
      } else {
        router.back();
      }
    }, [onBackPress]);

    // Check if we can go back for change password mode
    const isRoot = !pathname || pathname === "/";
    const navCanGoBack =
      typeof (navigation as any)?.canGoBack === "function"
        ? (navigation as any).canGoBack()
        : false;
    const shouldShowBack =
      (mode === "change" || mode === "forgot") && !isRoot && navCanGoBack;

    const title =
      mode === "set" ? t("auth.newPassword") : t("auth.enterNewPasswordTitle");
    const subtitle =
      mode === "set"
        ? t("auth.setNewPassword")
        : t("auth.pleaseEnterNewPassword");

    return (
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
      >
        {mode === "change" || mode === "forgot" ? (
          <BaseHeader
            leading={
              shouldShowBack ? (
                <TouchableOpacity
                  onPress={handleBackPress}
                  style={styles.backButtonContainer}
                >
                  <BackArrowIcon size={24} color={colors.fontPrimary} />
                </TouchableOpacity>
              ) : (
                <View style={styles.placeholder} />
              )
            }
            middle={<View style={styles.placeholder} />}
            trailing={<View style={styles.placeholder} />}
          />
        ) : (
          <LogoHeader showBackButton={true} onBackPress={handleBackPress} />
        )}

        <View
          style={[
            styles.content,
            { paddingTop: 16, paddingBottom: insets.bottom + 24 },
          ]}
        >
          <View style={styles.contentWrapper}>
            {/* Page Header */}
            <View style={styles.pageHeader}>
              {mode === "set" && (
                <View style={styles.iconContainer}>
                  <KeyRoundIcon size={40} />
                </View>
              )}
              <Typography
                variant="title1"
                color="fontPrimary"
                style={styles.title}
              >
                {title}
              </Typography>
              <Typography
                variant="body"
                color="fontPrimary"
                style={styles.subtitle}
              >
                {subtitle}
              </Typography>
            </View>

            {/* Password Input Section */}
            <View style={styles.inputSection}>
              {/* New Password Input */}
              <View style={styles.inputWrapper}>
                <TextInput
                  label={t("auth.enterNewPassword")}
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  placeholder={t("auth.enterNewPassword")}
                  rightIcon={
                    <Pressable onPress={() => setShowPassword(!showPassword)}>
                      <EyeIcon
                        size={19}
                        color={colors.fontPrimary}
                        isVisible={showPassword}
                      />
                    </Pressable>
                  }
                />

                {/* Password Validation Rules */}
                <View style={styles.validationRules}>
                  <View style={styles.validationItem}>
                    {passwordValidation.hasCapital ? (
                      <CircleCheckIcon
                        size={12}
                        strokeWidth={6}
                        color={colors.fontSuccessText}
                      />
                    ) : (
                      <CircleIcon size={12} color={colors.fontTertiary} />
                    )}
                    <Typography
                      variant="footnote"
                      color={
                        passwordValidation.hasCapital ? "success" : "tertiary"
                      }
                      style={styles.validationText}
                    >
                      {t("auth.passwordRuleCapital")}
                    </Typography>
                  </View>
                  <View style={styles.validationItem}>
                    {passwordValidation.hasSmall ? (
                      <CircleCheckIcon
                        size={12}
                        strokeWidth={6}
                        color={colors.fontSuccessText}
                      />
                    ) : (
                      <CircleIcon size={12} color={colors.fontTertiary} />
                    )}
                    <Typography
                      variant="footnote"
                      color={
                        passwordValidation.hasSmall ? "success" : "tertiary"
                      }
                      style={styles.validationText}
                    >
                      {t("auth.passwordRuleSmall")}
                    </Typography>
                  </View>
                  <View style={styles.validationItem}>
                    {passwordValidation.hasNumber ? (
                      <CircleCheckIcon
                        size={12}
                        strokeWidth={6}
                        color={colors.fontSuccessText}
                      />
                    ) : (
                      <CircleIcon size={12} color={colors.fontTertiary} />
                    )}
                    <Typography
                      variant="footnote"
                      color={
                        passwordValidation.hasNumber ? "success" : "tertiary"
                      }
                      style={styles.validationText}
                    >
                      {t("auth.passwordRuleNumber")}
                    </Typography>
                  </View>
                  <View style={styles.validationItem}>
                    {passwordValidation.hasSymbol ? (
                      <CircleCheckIcon
                        size={12}
                        strokeWidth={6}
                        color={colors.fontSuccessText}
                      />
                    ) : (
                      <CircleIcon size={12} color={colors.fontTertiary} />
                    )}
                    <Typography
                      variant="footnote"
                      color={
                        passwordValidation.hasSymbol ? "success" : "tertiary"
                      }
                      style={styles.validationText}
                    >
                      {t("auth.passwordRuleSymbol")}
                    </Typography>
                  </View>
                </View>
              </View>

              {/* Confirm Password Input */}
              <View style={styles.inputWrapper}>
                <TextInput
                  label={t("auth.confirmNewPassword")}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  secureTextEntry={!showConfirmPassword}
                  placeholder={t("auth.confirmNewPassword")}
                  rightIcon={
                    <Pressable
                      onPress={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      <EyeIcon
                        size={19}
                        color={colors.fontPrimary}
                        isVisible={showConfirmPassword}
                      />
                    </Pressable>
                  }
                />

                {/* Password Match Status */}
                {confirmPassword.length > 0 && (
                  <View style={styles.matchStatus}>
                    {passwordsMatch ? (
                      <CircleCheckIcon
                        size={12}
                        strokeWidth={6}
                        color={colors.fontSuccessText}
                      />
                    ) : (
                      <CircleIcon size={12} color={colors.fontTertiary} />
                    )}
                    <Typography
                      variant="footnote"
                      color={passwordsMatch ? "success" : "tertiary"}
                      style={styles.matchText}
                    >
                      {t("auth.passwordMatches")}
                    </Typography>
                  </View>
                )}
              </View>
            </View>

            {/* Confirm Button (directly below inputs) */}
            <View style={styles.buttonContainer}>
              <Button
                title={
                  loading
                    ? t("common.loading") || "Loading..."
                    : t("common.confirm")
                }
                variant="primary"
                fullWidth={true}
                onPress={handleConfirm}
                disabled={!isFormValid || loading}
                leading={
                  loading ? (
                    <ActivityIndicator size="small" color={colors.white} />
                  ) : undefined
                }
              />
            </View>
          </View>
        </View>

        {/* Error Dialog */}
        <OkDialog
          visible={errorDialogVisible}
          message={errorMessage}
          onOk={() => setErrorDialogVisible(false)}
        />
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  contentWrapper: {
    width: "100%",
    maxWidth: 600,
  },
  pageHeader: {
    gap: 16,
    marginTop: 32,
    marginBottom: 32,
  },
  iconContainer: {
    paddingHorizontal: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: "500",
    textAlign: "left",
  },
  subtitle: {
    textAlign: "left",
  },
  inputSection: {
    gap: 8,
    width: "100%", // Ensure section takes full width for RTL alignment
  },
  inputWrapper: {
    gap: 8,
    width: "100%", // Ensure wrapper takes full width for RTL alignment
  },
  validationRules: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    paddingHorizontal: 8,
  },
  validationItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    height: 14,
  },
  validationText: {
    fontSize: 13,
    lineHeight: 14,
  },
  matchStatus: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    height: 14,
    paddingHorizontal: 8,
  },
  matchText: {
    fontSize: 13,
    lineHeight: 14,
  },
  buttonContainer: {
    marginTop: 24,
    paddingBottom: 12,
  },
  backButtonContainer: {
    height: 40,
    width: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  placeholder: {
    height: 40,
    width: 40,
  },
});

export default PasswordSetScreen;
